import { api } from "@/api/client";

export type ProjectRole = "owner" | "developer" | "billing";
// Server accepts all three on invite; only owners can mint an
// "owner" invite (route-level gate).
export type InviteRole = ProjectRole;

export interface Project {
  id: string;
  name: string;
  plan_id: string;
  suspended: boolean;
  paid_until: string | null;
  created_at: string;
  role: ProjectRole;            // caller's role on this project
}

export interface Member {
  user_id: string;
  email: string;
  role: ProjectRole;
  joined_at: string;
}

export interface Invite {
  id: string;
  email: string;
  role: ProjectRole;
  expires_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

// Pay-then-project: POST /projects no longer creates a project. It
// creates an intent + a deposit address; the project is INSERTed
// later by process-renewals when the on-chain payment confirms.
export interface CreateProjectIntent {
  payment_id: string;
  // null for a 100%-off comp (no deposit needed).
  public_key: string | null;
  amount_usdc: string;
  expires_at: string | null;
  // When a promo zeroed the price the project is created inline; route
  // straight to project_id and skip the deposit step.
  is_comp: boolean;
  project_id: string | null;
}

export interface IntentStatus {
  payment_id: string;
  intent_name: string;
  status: "pending" | "confirmed" | "rejected" | "refunded";
  amount_usdc: string;
  project_id: string | null;
}

export interface IntentOnchain {
  public_key: string;
  amount_usdc: string;
  expires_at: string;
  status: "pending" | "received" | "expired";
}

export const projectsApi = {
  create: (name: string, plan_id: string, promo_code?: string) =>
    api.post<CreateProjectIntent>("/projects", {
      name,
      plan_id,
      ...(promo_code ? { promo_code } : {}),
    }),
  intentStatus: (payment_id: string) =>
    api.get<IntentStatus>(`/payments/intent/${payment_id}`),
  intentOnchain: (payment_id: string) =>
    api.get<IntentOnchain>(`/payments/intent/${payment_id}/onchain`),
  intentOnchainRefresh: (payment_id: string) =>
    api.post<IntentOnchain>(`/payments/intent/${payment_id}/onchain`, {}),
  get:    (pid: string)  => api.get<Project>(`/projects/${pid}`),
  rename: (pid: string, name: string) => api.patch<Project>(`/projects/${pid}`, { name }),
  remove: (pid: string)  => api.delete<{ ok: true }>(`/projects/${pid}`),

  listMembers: (pid: string) => api.get<Member[]>(`/projects/${pid}/members`),
  setMemberRole: (pid: string, uid: string, role: ProjectRole) =>
    api.patch<Member>(`/projects/${pid}/members/${uid}`, { role }),
  removeMember: (pid: string, uid: string) =>
    api.delete<{ ok: true }>(`/projects/${pid}/members/${uid}`),

  listInvites: (pid: string) => api.get<Invite[]>(`/projects/${pid}/invites`),
  invite: (pid: string, email: string, role: InviteRole) =>
    api.post<Invite>(`/projects/${pid}/invites`, { email, role }),
  revokeInvite: (pid: string, id: string) =>
    api.post<{ ok: true }>(`/projects/${pid}/invites/${id}/revoke`),
  resendInvite: (pid: string, id: string) =>
    api.post<Invite>(`/projects/${pid}/invites/${id}/resend`),

  acceptInvite: (token: string) => api.post<{ project_id: string }>("/invites/accept", { token }),
};
